import { decodeHTML } from "entities";

// Tags whose text content (and inline formatting) we keep when emitted by AI
// rich-text. Anything outside this list is unwrapped so its safe text stays.
const SANITIZE_ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "sub",
  "sup",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "hr",
  "span",
  "div",
  "a",
  "img",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td"
]);

// Tags we drop entirely (subtree included). Active-content vectors and tags
// that can pivot to scripting via attributes (svg/onload, math, srcdoc, etc).
const SANITIZE_DANGEROUS_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "link",
  "meta",
  "base",
  "svg",
  "math",
  "audio",
  "video",
  "source",
  "track",
  "picture"
]);

const SANITIZE_DROP_ATTRS = new Set(["srcdoc", "formaction", "xlink:href"]);

const SANITIZE_ALLOWED_STYLES = new Set([
  "color",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "width",
  "height",
  "max-width",
  "max-height"
]);

// ENG-2277 (HTML Forms): the "form" profile also keeps a Form's layout — its cell borders,
// backgrounds, padding and alignment, its tables' column widths, and its page breaks.
export type SanitizeProfile = "default" | "form";

type SanitizeOptions = { profile?: SanitizeProfile };

const FORM_ALLOWED_TAGS = new Set([
  ...SANITIZE_ALLOWED_TAGS,
  "colgroup",
  "col"
]);

const FORM_ALLOWED_STYLES = new Set([
  ...SANITIZE_ALLOWED_STYLES,
  "text-align",
  "vertical-align",
  "line-height",
  "text-decoration"
]);

// Every longhand of these is kept too (border-bottom-color, padding-left, ...).
const FORM_ALLOWED_STYLE_FAMILIES = [
  "border",
  "background",
  "padding",
  "margin"
];

const FORM_PAGE_BREAK = "<!-- pagebreak -->";
// Stands in for the page break while every other comment is stripped. Not valid markup, so no
// later step can match it as a tag.
const FORM_PAGE_BREAK_TOKEN = "\u0000pagebreak\u0000";

const isAllowedStyle = (property: string, profile: SanitizeProfile) => {
  if (profile === "default") return SANITIZE_ALLOWED_STYLES.has(property);
  return (
    FORM_ALLOWED_STYLES.has(property) ||
    FORM_ALLOWED_STYLE_FAMILIES.some(
      (family) => property === family || property.startsWith(`${family}-`)
    )
  );
};

// Void elements are written as `<tag/>` even if they came in unclosed.
// `img` is in `SANITIZE_ALLOWED_TAGS` and gated by `isSafeUrl` for its `src`.
const SANITIZE_VOID_ELEMENTS = new Set([
  "br",
  "col",
  "hr",
  "img",
  "input",
  "area",
  "base",
  "col",
  "embed",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
]);

// Strip HTML comments, processing instructions, CDATA, and DOCTYPE blocks.
// These cannot legitimately appear in AI-generated rich text and have been
// used historically as comment-eating XSS tricks. Split into separate REs
// to keep each one simple.
const SANITIZE_COMMENT_RE = /<!--[\s\S]*?-->/g;
const SANITIZE_PI_RE = /<\?[\s\S]*?\?>/g;
const SANITIZE_CDATA_RE = /<!\[CDATA\[[\s\S]*?\]\]>/g;
const SANITIZE_DOCTYPE_RE = /<!DOCTYPE[^>]*>/gi;

// Match an attribute inside a tag's attribute blob:
//   name="value" | name='value' | name=value | name (no value)
// Capture groups: 1=name, 2=double-quoted, 3=single-quoted, 4=unquoted.
const SANITIZE_ATTR_RE =
  /([a-zA-Z_:][\w:.-]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>=`]+)))?/g;

// Match any tag: open `<tag attrs>`, close `</tag>`, or self-closing `<tag/>`.
// Capture groups: 1=slash (close tag), 2=name, 3=attrs blob, 4=trailing slash.
const SANITIZE_TAG_RE =
  /<(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)\b([^>]*?)(\/?)\s*>/g;

function isSafeUrl(value: string | null): boolean {
  if (value == null) return false;
  const v = value.trim().toLowerCase();
  // eslint-disable-next-line no-script-url
  if (v.startsWith("javascript:") || v.startsWith("vbscript:")) return false;
  // Allow data: only for images; block data:text/html and friends.
  if (v.startsWith("data:") && !v.startsWith("data:image/")) return false;
  return true;
}

function escapeAttrValue(value: string): string {
  // Escape `&` first (else we'd double-encode the entities introduced below),
  // then quotes (which break out of attribute delimiters), then angle brackets
  // for defense-in-depth against downstream regex parsers and copy-paste into
  // unquoted contexts.
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

// ENG-2277: a Form's style value can hold a `;` of its own (an image carried as data,
// `url(data:image/png;base64,...)`), so a Form's declarations are split where a browser splits them:
// at a `;` outside brackets and quotes. Splitting at every `;` cut such an image in half.
function splitFormStyleDeclarations(styleValue: string): string[] {
  const declarations: string[] = [];
  let depth = 0;
  let quote = "";
  let start = 0;
  for (let index = 0; index < styleValue.length; index++) {
    const char = styleValue[index];
    if (quote) {
      if (char === quote) quote = "";
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    } else if (char === ";" && depth === 0) {
      declarations.push(styleValue.slice(start, index));
      start = index + 1;
    }
  }
  declarations.push(styleValue.slice(start));
  return declarations;
}

// ENG-2277: the form profile keeps backgrounds and borders, whose values can name a file to load. One
// on another site would be fetched each time the form opens, so the only file a Form's style names is
// an image carried in the style itself, as data: `url(data:image/...)`. Anything else that can name
// one goes with its declaration: any other url(), an image set, and a backslash, which can spell
// either of those so that it is not seen here.
const STYLE_DATA_IMAGE_URL_RE = /url\(\s*["']?\s*data:image\//gi;
const STYLE_FILE_LOADER_RE = /\\|url\(|image-set\(/i;

const loadsFromElsewhere = (declaration: string) =>
  STYLE_FILE_LOADER_RE.test(declaration.replace(STYLE_DATA_IMAGE_URL_RE, ""));

function filterStyleAttr(styleValue: string, profile: SanitizeProfile): string {
  const declarations =
    profile === "form"
      ? splitFormStyleDeclarations(styleValue)
      : styleValue.split(";");
  return declarations
    .map((s) => s.trim())
    .filter((s) => {
      if (!s) return false;
      const [property] = s.split(":").map((x) => x.trim());
      if (!isAllowedStyle(property.toLowerCase(), profile)) return false;
      return profile !== "form" || !loadsFromElsewhere(s);
    })
    .join("; ");
}

function cleanAttribute(
  rawName: string,
  rawValue: string,
  profile: SanitizeProfile
): string | null {
  const name = rawName.toLowerCase();
  if (name.startsWith("on")) return null;
  if (SANITIZE_DROP_ATTRS.has(name)) return null;

  // Decode entities so url validation isn't bypassed by `&#x6A;avascript:`.
  const decoded = decodeHTML(rawValue);

  if ((name === "href" || name === "src") && !isSafeUrl(decoded)) return null;

  if (name === "style") {
    const filtered = filterStyleAttr(decoded, profile);
    return filtered ? `${name}="${escapeAttrValue(filtered)}"` : null;
  }

  return `${name}="${escapeAttrValue(decoded)}"`;
}

function cleanOpenTag(
  tagName: string,
  attrsBlob: string,
  selfClosing: boolean,
  profile: SanitizeProfile
): string {
  if (!allowedTags(profile).has(tagName)) return "";

  let cleanedAttrs: string[] = [];
  let m: RegExpExecArray | null;
  SANITIZE_ATTR_RE.lastIndex = 0;
  m = SANITIZE_ATTR_RE.exec(attrsBlob);
  while (m !== null) {
    const value = m[2] ?? m[3] ?? m[4] ?? "";
    const cleaned = cleanAttribute(m[1], value, profile);
    if (cleaned) cleanedAttrs.push(cleaned);
    m = SANITIZE_ATTR_RE.exec(attrsBlob);
  }

  // Anchor hardening: drop any user-supplied `rel` and force the safe value.
  // Replacement (rather than token merging) is intentional — AI-generated
  // content has no legitimate need for custom rel tokens, and replacement
  // guarantees `noopener noreferrer` is present so window.opener tabnabbing
  // and referrer leaks are blocked even when `target="_blank"` is used.
  if (tagName === "a") {
    cleanedAttrs = cleanedAttrs.filter((a) => !a.startsWith("rel="));
    cleanedAttrs.push(`rel="noopener noreferrer"`);
  }

  const close =
    selfClosing || SANITIZE_VOID_ELEMENTS.has(tagName) ? "/" : "";
  const attrs = cleanedAttrs.length ? ` ${cleanedAttrs.join(" ")}` : "";
  return `<${tagName}${attrs}${close}>`;
}

function allowedTags(profile: SanitizeProfile): Set<string> {
  return profile === "form" ? FORM_ALLOWED_TAGS : SANITIZE_ALLOWED_TAGS;
}

function stripDangerousBlocks(html: string): string {
  let out = html;
  for (const tag of SANITIZE_DANGEROUS_TAGS) {
    // Non-greedy match of paired tags, including their content. Loop until
    // fixed-point so nested duplicates like <iframe><iframe>x</iframe></iframe>
    // are fully removed.
    const blockRe = new RegExp(
      String.raw`<${tag}\b[\s\S]*?</${tag}\s*>`,
      "gi"
    );
    let prev: string;
    do {
      prev = out;
      out = out.replace(blockRe, "");
    } while (out !== prev);
  }
  return out;
}

/**
 * Sanitize an HTML string into an allowlisted, attribute-scrubbed form safe
 * for rendering through `dangerouslySetInnerHTML` or pasting into TinyMCE.
 *
 * Pure-regex implementation (no `DOMParser`, no `<template>`). The trade-off
 * is well known: regex-based HTML sanitizers can be bypassed with sufficiently
 * malformed input. This implementation defends against the common attack
 * vectors (script blocks, event handlers, dangerous URL schemes, srcdoc) and
 * is intended for content that is already approximately well-formed — AI
 * backend output and contentEditable-normalized HTML. Do not use it on
 * arbitrary attacker-supplied raw HTML.
 */
export function sanitizeHTML(
  html: string,
  { profile = "default" }: SanitizeOptions = {}
): string {
  if (!html) return "";

  const keepPageBreaks = profile === "form";
  const tags = allowedTags(profile);

  // 1) Strip noise that can hide tags from the rest of the pipeline.
  let out = (
    keepPageBreaks
      ? html.replaceAll(FORM_PAGE_BREAK, FORM_PAGE_BREAK_TOKEN)
      : html
  )
    .replace(SANITIZE_COMMENT_RE, "")
    .replace(SANITIZE_PI_RE, "")
    .replace(SANITIZE_CDATA_RE, "")
    .replace(SANITIZE_DOCTYPE_RE, "");

  // 2) Remove paired dangerous tags WITH their content.
  out = stripDangerousBlocks(out);

  // 3) Walk every remaining tag: clean allowlisted ones, drop everything
  //    outside the allowlist (close tags + orphan/self-closing dangerous
  //    tags fall through to the empty-string branch).
  out = out.replace(
    SANITIZE_TAG_RE,
    (_match, slash: string, rawName: string, attrs: string, selfClose: string) => {
      const name = rawName.toLowerCase();
      if (SANITIZE_DANGEROUS_TAGS.has(name)) return "";
      if (slash) return tags.has(name) ? `</${name}>` : "";
      return cleanOpenTag(name, attrs, Boolean(selfClose), profile);
    }
  );

  return keepPageBreaks
    ? out.replaceAll(FORM_PAGE_BREAK_TOKEN, FORM_PAGE_BREAK)
    : out;
}
